import { FC, useEffect, useMemo, useRef } from "react";
import { Row } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { Scrollbars } from "../../Scrollbars";
import { useTableContext, useTablePropsContext } from "../context";
import { useSettingsContext } from "../ControlBar";
import { useColumnSizeScaling } from "../utils";
import { HeaderCell } from "./HeaderCell";
import { TableCell } from "./TableCell";
import { TableOverlay } from "./TableOverlay";
import { getCommonPinningStyles, renderCells } from "./utils";

import styles from "./styles.module.scss";

export type BodyProps = {
  isFetching: boolean
}

export const Body: FC<BodyProps> = (({ isFetching }) => {
  const table = useTableContext();
  const { hideHeaders, useVirtualizeColumns, alternating, getRowProps } = useTablePropsContext();
  const { rowView } = useSettingsContext();
  const scrollbarRef = useRef<OverlayScrollbarsComponent>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  const leafHeaders = table.getLeafHeaders();
  const headers = useMemo(() => leafHeaders
    .filter(x => !x.subHeaders.length), [ leafHeaders ]);
  const columns = useMemo(() => headers.map(x => x.column), [ headers ]);
  const columnVirtualizer = useVirtualizer({
    count: headers.length,
    estimateSize: index => headers[index].getSize(),
    getScrollElement: () => scrollbarRef.current?.osInstance()
      ?.getElements().viewport!,
    horizontal: true,
    overscan: 1,
  });
  const columnSizing = table.getState().columnSizing;
  useEffect(() => {
    if (useVirtualizeColumns) {
      columnVirtualizer.measure();
    }
  }, [ useVirtualizeColumns, columnVirtualizer, columnSizing ]);

  const pageSize = table.getState().pagination.pageSize;
  let rows = table.getRowModel().rows;
  if (rows.length < pageSize) {
    rows = rows.concat(Array(pageSize)
      .fill(null))
      .slice(0, pageSize);
  }
  const rowMinHeight =
    Number(getComputedStyle(tbodyRef.current || document.body)
      .getPropertyValue("--table-row-min-height")
      .replaceAll(/\D/gi, "")) || (rowView === "regular" ? 56 : 33);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowMinHeight,
    getScrollElement: () => scrollbarRef.current?.osInstance()
      ?.getElements().viewport!,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? element => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const bodyHeight = rowMinHeight * pageSize;
  const headerHeight = !hideHeaders && theadRef.current ?  theadRef.current.clientHeight : 0;

  useColumnSizeScaling(table.getTotalSize(), scrollbarRef.current?.osTarget()?.clientWidth || 0)

  if (!table) {
    return null;
  }

  return (
    <div className={styles.root}>
      <Scrollbars
        key="scrollbars"
        className={classNames("os-host-flexbox")}
        ref={scrollbarRef}
        style={{ height: bodyHeight + headerHeight }}
      >
        <table
          style={{ width: table.getTotalSize() }}
          className={classNames(
            styles.table,
            styles[rowView as keyof typeof styles],
            alternating ? styles.alternating : styles.default,
          )}
        >
          {
            !hideHeaders &&
            <thead ref={theadRef}>
              <tr>
                {
                  renderCells(
                    columnVirtualizer,
                    useVirtualizeColumns,
                    columns,
                    index => {
                      const header = headers[index];

                      return (
                        <HeaderCell
                          key={header.id}
                          header={header}
                          style={{ ...getCommonPinningStyles(header.column) }}
                        />
                      );
                    },
                    HeaderCell,
                  )
                }
              </tr>
            </thead>
          }
          <tbody
            ref={tbodyRef}
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {rowVirtualizer.getVirtualItems()
              .map(virtualRow => {
                const row = rows[virtualRow.index] as Row<Object>;
                const rowAdditionalProps = getRowProps?.(row) || {};
                return (
                  <tr
                    data-index={virtualRow.index}
                    ref={node => rowVirtualizer.measureElement(node)}
                    key={row?.id ?? `fake_row_${virtualRow.index}`}
                    data-selected-row={row?.getIsSelected()}
                    data-even-row={virtualRow.index % 2 === 0}
                    {...rowAdditionalProps}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      ...rowAdditionalProps?.style
                    }}
                  >
                    {
                      renderCells(
                        columnVirtualizer,
                        useVirtualizeColumns,
                        columns,
                        index => {
                          const cell = row?.getVisibleCells()[index];

                          return cell ? (
                            <TableCell
                              key={cell.id}
                              cell={cell}
                              style={{ ...getCommonPinningStyles(cell.column) }}
                            />
                          ) : null;
                        },
                        TableCell,
                      )
                    }
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Scrollbars>
      <TableOverlay
        isFetching={isFetching}
        bodyHeight={bodyHeight}
        headerHeight={headerHeight}
      />
    </div>
  );
});